import { ContextKey } from '@pubstack/common/src/contextKey'
import { StackContext } from '@pubstack/common/src/stackContext'
import { FunctionComponent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useContextKeys } from '~/api/adm-api.hook'
import { contextNameErrorMessage, useCreateContext, useStackContext } from '~/api/adm/contexts.api'
import { useToast } from '~/components/Toast/useToasts'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { PureAdmContextEditPage } from './PureAdmContextEditPage'

const emptyContext: StackContext = {
  id: '',
  name: '',
  conditions: { op: 'AND', elements: [] },
  scopeId: '',
  creationTime: 0,
  enabled: true,
}

export const AdmContextEditPage: FunctionComponent = () => {
  const navigate = useNavigate()
  const [contextKeys, setContextKeys] = useState<ContextKey[]>([])
  const { contextId = '' } = useParams()
  const toast = useToast()
  const { data: fetchedContext } = useStackContext(contextId)
  const context = fetchedContext ?? emptyContext
  const { all } = useContextKeys(null)
  const breadcrumbs = useBreadcrumbs(context)
  // No `errorToast`: a name already taken belongs under the name field, and a toast saying so as well would
  // report the same refusal twice, in the one place the author cannot fix it from.
  const { mutateAsync: createContext } = useCreateContext()

  async function loadContextKeys() {
    try {
      const contextKeys = await all.get()
      setContextKeys(contextKeys)
    } catch (_e) {
      toast.alert('An error occurred while fetching contextKeys for this scope.')
    }
  }

  async function onCreateContext(context: StackContext) {
    try {
      const newContext = await createContext(context)
      toast.success(`${newContext.name} added to your inventory.`)
      navigate('/monetization/one/context/contexts')
    } catch (error) {
      // A named field can carry its own reason, so the form is left to show it. Everything else has nowhere
      // on the page to sit and stays a toast.
      if (contextNameErrorMessage(error)) throw error
      toast.alert('An error occurred while saving this context.')
    }
  }

  useEffect(() => {
    // Legitimate data load on mount.
    // oxlint-disable-next-line react/set-state-in-effect
    loadContextKeys()
  }, [])

  return <PureAdmContextEditPage isEditing={!!context?.id} context={context} contextKeys={contextKeys} breadcrumbs={breadcrumbs} createContext={onCreateContext} onBack={() => navigate(-1)} />
}
